import {
  Table,
  ExpandableSection,
  Grid,
  Button,
  Header,
  Box,
  TableProps,
  NonCancelableCustomEvent,
  Alert,
  Spinner,
  SpaceBetween,
  Modal,
} from '@amzn/awsui-components-core';
import {
  APGLUserProfile,
  ContentAccessStatus,
  ContentAccessUser,
  ContentWorkflow,
  UserType,
} from '@amzn/awscat-pattern-library-service-model';
import React, { useEffect, useState, useContext, useMemo } from 'react';

import { IMplServiceClient } from '../../api/IMplServiceClient';
import SubmitForReviewControl from '../workflow-viewer/content/SubmitForReviewControl';
import { MplServiceClientFactory } from '../../api/MplServiceClientFactory';
import { AppContext } from '../../contexts/AppContext';
import UserAccessPermissionForm from '../common/UserAccessPermissionForm';
import FlashbarMessageBroker from '../../common/FlashbarMessageBroker';
import { maxNumberOfPeerReviewers, convertReviewersNumberToText } from '../../api/UtilsApi';
import arraySort from 'array-sort';
import {
  awsServicesCol,
  namesCol,
  reviewsCompletedCol,
  reviewsInProgressCol,
  reviewsPendingAcceptanceCol,
  techDomainsCol,
  typesCol,
} from '../apgl-user-profile/PeerReviewerTableColumnDefinitions';
import { notAllowedToDeleteReviewerAlert } from '../workflow-viewer/content/ContentUtils';
import constants from '../../common/constants';
import { isInPeerReview } from '../utils/ContentDataUtils';

interface Props {
  workflow: ContentWorkflow;
  reviewers?: ContentAccessUser[];
  onReviewersUpdated?: (reviewers: ContentAccessUser[]) => void;
  readonly?: boolean;
  isEditReviewersDisabled?: boolean;
  isDeleteReviewersDisabled?: boolean;
  readView?: boolean;
  showCannotDeleteReviewerAlert?: boolean;
  isSubmitForReviewDisabled: boolean;
  isContentUnchanged: boolean;
  handleSaveChanges: (unlock: boolean) => Promise<any>;
  handleSubmitForReview: () => Promise<any>;
  validateSubmitForReview: () => Promise<boolean>;
}

const ContentReviewers = ({
  workflow,
  onReviewersUpdated,
  readonly,
  isEditReviewersDisabled,
  isDeleteReviewersDisabled,
  readView,
  reviewers: reviewersFromProps,
  showCannotDeleteReviewerAlert,
  isSubmitForReviewDisabled,
  isContentUnchanged,
  handleSaveChanges,
  handleSubmitForReview,
  validateSubmitForReview,
}: Props) => {
  const mplServiceClient: IMplServiceClient = useMemo(() => MplServiceClientFactory.create(), []);
  const flashbarMessageBroker: FlashbarMessageBroker = useMemo(
    () => FlashbarMessageBroker.create(),
    [],
  );

  const [reviewers, setReviewers] = useState<ContentAccessUser[]>(reviewersFromProps || []);
  const [reviewerProfiles, setReviewerProfiles] = useState<APGLUserProfile[]>([]); // items in added reviewers table
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isFetchingReviewerList, setIsFetchingReviewerList] = useState<boolean>(false);
  const [selectedReviewers, setSelectedReviewers] = useState<APGLUserProfile[]>([]);
  // @ts-expect-error TS2345 - strict migration
  const [sortingColumn, setSortingColumn] = useState<TableProps.SortingColumn<any>>(null);
  const [sortingDescending, setSortingDescending] = useState<boolean>(false);
  const [showAdditionalReviewerModal, setShowAdditionalReviewerModal] = useState<boolean>(false);
  const [reviewersToAdd, setReviewersToAdd] = useState<string[]>([]);
  const appContext = useContext(AppContext);
  // @ts-expect-error TS18048 - strict migration
  const { type } = appContext.authnInfo.userProfile;

  const addedReviewersColumnDefinitions: ReadonlyArray<TableProps.ColumnDefinition<any>> =
    type === UserType.AWS
      ? [namesCol, typesCol, reviewsCompletedCol, reviewsPendingAcceptanceCol, reviewsInProgressCol]
      : [namesCol, typesCol];

  const initReviewers = async () => {
    try {
      setIsFetchingReviewerList(true);
      await fetchReviewerInfo();
    } finally {
      setIsFetchingReviewerList(false);
    }
  };

  useEffect(() => {
    if (!reviewers || !reviewers.length) {
      initReviewers();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadReviewers = async () => {
    const accessUsers = await fetchReviewerInfo();
    if (onReviewersUpdated) {
      onReviewersUpdated(accessUsers);
    }
  };

  const fetchReviewerInfo = async () => {
    const { contentId } = workflow;
    let reviewersInfo = [];
    try {
      setIsLoading(true);
      const { accessUsers } = await mplServiceClient.listContentReviewers(contentId);
      reviewersInfo = accessUsers;
      const profiles = constructReviewerProfiles(reviewersInfo);
      if (type === UserType.AWS) {
        arraySort(profiles, 'reviewsInProgress', { reverse: false });
        setSortingColumn(
          // @ts-expect-error TS2345 - strict migration
          addedReviewersColumnDefinitions.find((c) => c.sortingField === 'reviewsInProgress'),
        );
        setSortingDescending(false);
      }
      setReviewers(reviewersInfo);
      setReviewerProfiles(profiles);
    } finally {
      setIsLoading(false);
    }
    return reviewersInfo;
  };

  const handleSortingChange = (event: NonCancelableCustomEvent<TableProps.SortingState<any>>) => {
    const updatedSortingDescending = event.detail.isDescending;
    const updatedSortingColumn = event.detail.sortingColumn;
    setSortingColumn(updatedSortingColumn);
    // @ts-expect-error TS2345 - strict migration
    setSortingDescending(updatedSortingDescending);
    // @ts-expect-error TS2345 - strict migration
    sortColumn(updatedSortingColumn, updatedSortingDescending);
  };

  const sortColumn = (
    updatedSortingColumn: TableProps.SortingColumn<any>,
    updatedIsDescending: boolean,
  ) => {
    const updatedReviewerProfiles = reviewerProfiles;
    arraySort(updatedReviewerProfiles, updatedSortingColumn.sortingField, {
      reverse: updatedIsDescending,
    });
    setReviewerProfiles(updatedReviewerProfiles);
  };

  const constructReviewerProfiles = (accessUsers: ContentAccessUser[]) => {
    return accessUsers.map((user) => {
      return Object.assign(new APGLUserProfile(), {
        userId: user.userId,
        name: user.userName,
        type: user.userType,
        totalReviewedContent: user.reviewsCompleted,
        reviewsPendingAcceptance: user.reviewsPendingAcceptance,
        reviewsInProgress: user.reviewsInProgress,
      });
    });
  };

  const updateReviewersFromSearch = async (reviewersToUpdate: ContentAccessUser[]) => {
    const newUserIds = reviewersToUpdate
      .filter((us) => us.status === ContentAccessStatus.PENDING_ADD)
      .map((u) => u.userId);
    await preUpdateReviewers(newUserIds);
  };

  const preUpdateReviewers = async (userIds: string[]) => {
    if (!reviewers.length && userIds.length === 1) {
      await addPeerReviewers(userIds);
    } else {
      setReviewersToAdd(userIds);
      setShowAdditionalReviewerModal(true);
    }
  };

  const addPeerReviewers = async (userIds: string[]) => {
    const { contentId } = workflow;
    setIsLoading(true);
    try {
      await mplServiceClient.addContentReviewers([contentId], userIds);
    } catch (e) {
      // @ts-expect-error TS2339 - strict migration
      flashbarMessageBroker.pushErrorMessage(e?.response?.data?.message);
    } finally {
      setIsLoading(false);
      await loadReviewers();
    }
  };

  const removeSelectedReviewers = async () => {
    const removedUserIds = selectedReviewers.map((reviewer) => reviewer.userId);
    const { contentId } = workflow;
    if (removedUserIds.length) {
      setIsLoading(true);
      await mplServiceClient.removeContentReviewers([contentId], removedUserIds);
      await loadReviewers();
      setIsLoading(false);
    }
    setSelectedReviewers([]);
  };

  const confirmAddAdditionalReviewers = async () => {
    await addPeerReviewers(reviewersToAdd);
    setReviewersToAdd([]);
    setShowAdditionalReviewerModal(false);
  };

  const cancelAddAdditionalReviewers = () => {
    setReviewersToAdd([]);
    setShowAdditionalReviewerModal(false);
    setReviewers([...reviewers]);
  };

  const addedReviewerTableAction =
    !readonly && !readView ? (
      <Box>
        <SpaceBetween direction="horizontal" size="xs">
          <Button
            disabled={!selectedReviewers.length || isDeleteReviewersDisabled}
            onClick={removeSelectedReviewers}
          >
            Remove
          </Button>
          {isInPeerReview(workflow) ? null : (
            <SubmitForReviewControl
              isContentUnchanged={isContentUnchanged}
              onSaveChanges={handleSaveChanges}
              onSubmitForReview={handleSubmitForReview}
              validateSubmitForReview={validateSubmitForReview}
              fullwidth
              disabled={isSubmitForReviewDisabled}
              workflow={workflow}
            />
          )}
        </SpaceBetween>
      </Box>
    ) : null;

  const addedReviewerHeader = (
    <>
      <Header
        variant="h3"
        counter={
          selectedReviewers.length
            ? `(${selectedReviewers.length}/${reviewers.length})`
            : `(${reviewers.length})`
        }
        actions={addedReviewerTableAction}
      >
        Added reviewers
      </Header>
      {type === UserType.Partner && (
        <Alert>Please add at least one AWS employee as a peer reviewer.</Alert>
      )}
    </>
  );

  const saerchAndAddReviewer =
    readonly || readView ? null : (
      <Grid gridDefinition={[{ colspan: 7 }, { colspan: 5 }]}>
        <UserAccessPermissionForm
          formId={'ReviewerForm'}
          contentId={workflow.contentId}
          contentName={workflow.draftContentMetadata.contentName}
          ownerId={workflow.ownerId}
          onAccessUpdate={updateReviewersFromSearch}
          accessUsers={reviewers}
          max={maxNumberOfPeerReviewers}
          isAddDisabled={isEditReviewersDisabled}
          isDeleteDisabled={isDeleteReviewersDisabled}
          hideReviewersTable
        />
      </Grid>
    );

  const emptyReviewers = (
    <Box textAlign="center">
      <Box fontWeight="bold">No reviewers added</Box>
      <Box>All requested reviewers who accept the review will be required to approve</Box>
      <Box>your content before it can be published</Box>
    </Box>
  );

  const bottomCannotDeleteReviewerAlert = showCannotDeleteReviewerAlert ? (
    <Box margin={{ top: 's' }}>{notAllowedToDeleteReviewerAlert}</Box>
  ) : null;

  const renderAdditionalReviewModal = () => {
    if (!showAdditionalReviewerModal) {
      return null;
    }
    const addAdditionalReviewerModalFooter = (
      <Box float="right">
        <SpaceBetween direction="horizontal" size="xs">
          <Button onClick={cancelAddAdditionalReviewers}>Cancel</Button>
          <Button variant="primary" onClick={confirmAddAdditionalReviewers} loading={isLoading}>
            Confirm
          </Button>
        </SpaceBetween>
      </Box>
    );
    const totalReviewers = reviewersToAdd.length + reviewers.length;
    const reviewerNumberText = convertReviewersNumberToText(totalReviewers);
    const newAddedReviewersNumberText = convertReviewersNumberToText(reviewersToAdd.length);
    return (
      <Modal
        onDismiss={() => setShowAdditionalReviewerModal(false)}
        visible={showAdditionalReviewerModal}
        header="Additional review approvals required"
        footer={addAdditionalReviewerModalFooter}
        closeAriaLabel="Close modal"
      >
        {`You have added ${newAddedReviewersNumberText} additional reviewer${reviewersToAdd.length > 1 ? 's' : ''}
                    to your content. If the reviewer accepts, your content will now require ${reviewerNumberText} approvals before publication. This may increase time to publication. Do you want to proceed?`}
      </Modal>
    );
  };

  return (
    <Box margin={{ bottom: 'm' }}>
      <Box padding={{ bottom: 'm' }}>{constants.ADD_PEER_REVIEWERS_DESC}</Box>
      <Table
        header={addedReviewerHeader}
        loading={isLoading || isFetchingReviewerList}
        columnDefinitions={addedReviewersColumnDefinitions}
        // @ts-expect-error TS2322 - strict migration
        sortingColumn={type === UserType.AWS ? sortingColumn : null}
        sortingDescending={sortingDescending}
        onSortingChange={handleSortingChange}
        empty={emptyReviewers}
        items={reviewerProfiles}
        onSelectionChange={({ detail }) => setSelectedReviewers(detail.selectedItems)}
        selectedItems={selectedReviewers}
        selectionType="multi"
        // @ts-expect-error TS2322 - strict migration
        isItemDisabled={(item) => readonly || readView}
        resizableColumns
        wrapLines
        filter={saerchAndAddReviewer}
        ariaLabels={{ tableLabel: 'Content reviewers table' }}
      />
      {bottomCannotDeleteReviewerAlert}
      {renderAdditionalReviewModal()}
    </Box>
  );
};

export default ContentReviewers;
